import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createClient } from '@/app/utils/supabase/client';
import { websocketService } from '@/app/lib/services/websocketService';
import { PageContext } from '@/app/lib/hooks/usePageContext';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  tools_used?: string[];
  processing_time_ms?: number;
  context?: PageContext; // Store context with user messages
  tool_call_id?: string; // Link to tool execution for system messages
}

export interface ConfirmationRequest {
  id: string;
  tool: string;
  action: string;
  details: Record<string, any>;
  timestamp: Date;
  tool_call_id?: string; // Added to link with tool execution
}

export interface ToolExecution {
  tool_call_id: string;
  tool_name: string;
  status: 'started' | 'completed' | 'failed';
  failure_type?: 'timeout' | 'denied' | 'error';
  reason?: string;
  result?: string;
  timestamp: Date;
  requires_confirmation: boolean;
}

type ConnectionState =
  | 'offline'
  | 'creating_session'
  | 'connecting_websocket'
  | 'connected'
  | 'error';

interface QueuedMessage {
  content: string;
  context?: PageContext;
  timestamp: Date;
}

interface CopilotStore {
  // UI State
  isOpen: boolean;
  panelWidth: number;

  // Session State
  sessionId: string | null;
  threadId: string | null;
  messages: Message[];
  confirmationRequests: Map<string, ConfirmationRequest>;
  toolExecutions: Map<string, ToolExecution>;
  uploadedImages: Map<string, string>; // tool_call_id -> image_url for cleanup
  messageQueue: QueuedMessage[];

  // Connection State
  connectionState: ConnectionState;
  isConnected: boolean;
  isConnecting: boolean;
  isTyping: boolean;
  connectionError: string | null;

  // Actions - UI
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  setPanelWidth: (width: number) => void;

  // Actions - Session Management
  createSession: (companyId: string, sessionName?: string) => Promise<void>;

  // Actions - WebSocket Management
  connect: () => Promise<void>;
  disconnect: () => void;

  // Actions - Messaging
  sendMessage: (content: string, context?: PageContext) => void;
  handleConfirmation: (confirmationId: string, approved: boolean, reason?: string, data?: any) => void;
  
  // Actions - Image Management
  storeUploadedImage: (toolCallId: string, imageUrl: string) => void;

  // Actions - Session
  startNewChat: () => Promise<void>;

  // Actions - Cleanup
  reset: () => void;
}

const INITIAL_STATE = {
  isOpen: false,
  panelWidth: 400,
  sessionId: null,
  threadId: null,
  messages: [],
  confirmationRequests: new Map(),
  toolExecutions: new Map(),
  uploadedImages: new Map(),
  messageQueue: [],
  connectionState: 'offline' as ConnectionState,
  isConnected: false,
  isConnecting: false,
  isTyping: false,
  connectionError: null,
};

export const useCopilotStore = create<CopilotStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      // UI Actions
      togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),

      openPanel: async () => {
        console.log('[CopilotStore] Opening panel...');
        const state = get();
        set({ isOpen: true });

        // If no session exists, create one when opening
        if (!state.sessionId && state.connectionState !== 'creating_session' && state.connectionState !== 'connecting_websocket') {
          console.log('[CopilotStore] No session, creating new one...');
          const { selectedCompanyId } = await import('@/app/lib/stores/useAppStore').then(m => m.useAppStore.getState());
          if (selectedCompanyId) {
            await get().createSession(selectedCompanyId);
          } else {
            set({ connectionState: 'error', connectionError: 'No company selected' });
          }
        } else if (state.sessionId && state.connectionState === 'offline') {
          console.log('[CopilotStore] Session exists, reconnecting...');
          await get().connect();
        }
      },

      closePanel: () => set({ isOpen: false }),

      setPanelWidth: (width: number) => {
        const clampedWidth = Math.max(320, Math.min(800, width));
        set({ panelWidth: clampedWidth });
      },

      // Session Management
      createSession: async (companyId: string, sessionName?: string) => {
        console.log('[CopilotStore] Creating session for company:', companyId);
        try {
          set({
            connectionState: 'creating_session',
            isConnecting: true,
            connectionError: null
          });

          const supabase = createClient();
          const { data: { session } } = await supabase.auth.getSession();

          if (!session) {
            throw new Error('No authenticated session');
          }

          // Create AI session
          const url = `${process.env.NEXT_PUBLIC_AI_BACKEND_URL || 'http://localhost:8000'}/api/v1/ai/sessions`;
          console.log('[CopilotStore] Creating session at:', url);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              company_id: companyId,
              name: sessionName || `Chat ${new Date().toLocaleDateString()}`,
            }),
          });

          if (!response.ok) {
            const error = await response.text();
            throw new Error(`Failed to create session: ${error}`);
          }

          const data = await response.json();
          console.log('[CopilotStore] Session created:', data.session_id);

          set({
            sessionId: data.session_id,
            threadId: data.thread_id,
            messages: [],
            confirmationRequests: new Map(),
          });

          // Connect WebSocket
          await get().connect();
        } catch (error) {
          console.error('[CopilotStore] Error creating session:', error);
          set({
            connectionState: 'error',
            connectionError: error instanceof Error ? error.message : 'Failed to create session',
            isConnecting: false,
          });
        }
      },

      // WebSocket Management
      connect: async () => {
        const state = get();

        if (!state.sessionId) {
          console.error('[CopilotStore] No session ID, cannot connect');
          return;
        }

        if (state.connectionState === 'connected' || state.connectionState === 'connecting_websocket') {
          console.log('[CopilotStore] Already connected or connecting');
          return;
        }

        try {
          set({
            connectionState: 'connecting_websocket',
            isConnecting: true,
            connectionError: null
          });

          const supabase = createClient();
          const { data: { session } } = await supabase.auth.getSession();

          if (!session) {
            throw new Error('No authenticated session');
          }

          console.log('[CopilotStore] Connecting WebSocket...');

          // Connect using the service
          await websocketService.connect(
            state.sessionId,
            session.access_token,
            {
              onOpen: () => {
                console.log('[CopilotStore] WebSocket opened');
              },

              onAuthSuccess: () => {
                console.log('[CopilotStore] Authentication successful');
                set({
                  connectionState: 'connected',
                  isConnected: true,
                  isConnecting: false,
                  connectionError: null,
                });

                // Process queued messages
                const state = get();
                if (state.messageQueue.length > 0) {
                  console.log('[CopilotStore] Processing', state.messageQueue.length, 'queued messages');
                  state.messageQueue.forEach((queuedMsg) => {
                    websocketService.sendMessage(queuedMsg.content, queuedMsg.context);
                  });
                  // Clear the queue
                  set({ messageQueue: [] });
                }
              },

              onAuthError: (message) => {
                console.error('[CopilotStore] Auth error:', message);
                set({
                  connectionState: 'error',
                  isConnected: false,
                  isConnecting: false,
                  connectionError: message,
                });
              },

              onHistory: (messages) => {
                console.log('[CopilotStore] Received history:', messages.length, 'messages');
                set({ messages });
              },

              onAssistantMessage: (message) => {
                console.log('[CopilotStore] Received assistant message');
                set((state) => ({
                  messages: [...state.messages, message],
                  isTyping: false,
                }));
              },

              onConfirmationRequest: (request) => {
                console.log('[CopilotStore] Received confirmation request');
                set((state) => {
                  const newRequests = new Map(state.confirmationRequests);
                  newRequests.set(request.id, request);
                  return { confirmationRequests: newRequests };
                });
              },

              onToolExecutionStarted: (execution) => {
                console.log('[CopilotStore] Tool execution started:', execution.tool_name);
                set((state) => {
                  const newExecutions = new Map(state.toolExecutions);
                  newExecutions.set(execution.tool_call_id, execution);
                  return { toolExecutions: newExecutions };
                });
              },

              onToolExecutionFailed: (execution) => {
                console.log('[CopilotStore] Tool execution failed:', execution.tool_name);
                
                // Handle image cleanup for failed tool executions
                const cleanupImage = async (state: any) => {
                  const imageUrl = state.uploadedImages.get(execution.tool_call_id);
                  if (imageUrl) {
                    try {
                      // Import the utilities and hooks dynamically to avoid circular dependencies
                      const { parseSupabaseStorageUrl } = await import('@/app/lib/utils/supabaseStorageUtils');
                      const { storageApi } = await import('@/app/lib/api/storage');
                      
                      const urlInfo = parseSupabaseStorageUrl(imageUrl);
                      if (urlInfo) {
                        await storageApi.deleteFile(urlInfo.bucket, urlInfo.path);
                      }
                    } catch (error) {
                      console.error('[CopilotStore] Failed to cleanup orphaned image:', error);
                    }
                  }
                };
                
                set((state) => {
                  // Trigger cleanup (async, won't block state update)
                  cleanupImage(state);
                  
                  const newExecutions = new Map(state.toolExecutions);
                  // Preserve requires_confirmation from started event
                  const existing = state.toolExecutions.get(execution.tool_call_id);
                  if (existing) {
                    execution.requires_confirmation = existing.requires_confirmation;
                  }
                  newExecutions.set(execution.tool_call_id, execution);

                  // Remove confirmation request if it exists
                  const newRequests = new Map(state.confirmationRequests);
                  for (const [id, req] of newRequests) {
                    if (req.tool_call_id === execution.tool_call_id) {
                      newRequests.delete(id);
                      break;
                    }
                  }
                  
                  // Remove uploaded image from tracking
                  const newUploadedImages = new Map(state.uploadedImages);
                  newUploadedImages.delete(execution.tool_call_id);

                  return {
                    toolExecutions: newExecutions,
                    confirmationRequests: newRequests,
                    uploadedImages: newUploadedImages
                  };
                });
              },

              onToolExecutionCompleted: (execution) => {
                console.log('[CopilotStore] Tool execution completed:', execution.tool_name);
                set((state) => {
                  const newExecutions = new Map(state.toolExecutions);
                  // Preserve requires_confirmation from started event
                  const existing = state.toolExecutions.get(execution.tool_call_id);
                  if (existing) {
                    execution.requires_confirmation = existing.requires_confirmation;
                  }
                  newExecutions.set(execution.tool_call_id, execution);

                  // Remove confirmation request if it exists
                  const newRequests = new Map(state.confirmationRequests);
                  for (const [id, req] of newRequests) {
                    if (req.tool_call_id === execution.tool_call_id) {
                      newRequests.delete(id);
                      break;
                    }
                  }

                  // Remove uploaded image from tracking (successful execution, no cleanup needed)
                  const newUploadedImages = new Map(state.uploadedImages);
                  newUploadedImages.delete(execution.tool_call_id);
                  
                  return {
                    toolExecutions: newExecutions,
                    confirmationRequests: newRequests,
                    uploadedImages: newUploadedImages
                  };
                });
              },

              onInvalidationRequest: async (entities) => {
                console.log('[CopilotStore] Received invalidation request for entities:', entities);
                // Note: We'll need to get queryClient from the app context
                // This will be handled in the component that initializes the store
              },

              onProcessingError: (errorMessage) => {
                console.error('[CopilotStore] Processing error:', errorMessage);
                // Stop typing animation
                set((state) => ({
                  isTyping: false,
                  // Add error message to chat
                  messages: [...state.messages, {
                    id: `error-${Date.now()}`,
                    role: 'assistant',
                    content: 'I encountered an error while processing your request. Please try again',
                    timestamp: new Date(),
                    tools_used: [],
                  }],
                }));
              },

              onError: (error) => {
                console.error('[CopilotStore] WebSocket error:', error);
                set({
                  connectionState: 'error',
                  connectionError: 'Connection error',
                  isConnecting: false,
                });
              },

              onClose: () => {
                console.log('[CopilotStore] WebSocket closed');
                set({
                  connectionState: 'offline',
                  isConnected: false,
                  isConnecting: false,
                });
              },
            }
          );
        } catch (error) {
          console.error('[CopilotStore] Error connecting:', error);
          set({
            connectionState: 'error',
            connectionError: error instanceof Error ? error.message : 'Failed to connect',
            isConnecting: false,
          });
        }
      },

      disconnect: () => {
        console.log('[CopilotStore] Disconnecting');
        websocketService.disconnect();
        set({
          connectionState: 'offline',
          isConnected: false,
          isConnecting: false,
        });
      },

      // Messaging
      sendMessage: (content: string, context?: PageContext) => {
        const state = get();

        // Add user message to UI immediately with context
        const userMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'user',
          content,
          timestamp: new Date(),
          context, // Store context with the message
        };

        set((state) => ({
          messages: [...state.messages, userMessage],
          isTyping: true,
        }));

        if (!state.isConnected) {
          console.log('[CopilotStore] Not connected, queuing message');
          // Add to queue
          set((state) => ({
            messageQueue: [...state.messageQueue, {
              content,
              context,
              timestamp: new Date(),
            }],
          }));
          return;
        }

        // Send via service with context
        websocketService.sendMessage(content, context);
      },

      handleConfirmation: (confirmationId: string, approved: boolean, reason?: string, data?: any) => {
        const state = get();


        if (!state.isConnected) {
          console.error('[CopilotStore] Not connected');
          return;
        }

        // Get the confirmation request to find tool_call_id
        const confirmationRequest = state.confirmationRequests.get(confirmationId);

        // Send confirmation
        websocketService.sendConfirmationResponse(confirmationId, approved, reason, data);

        // Remove from pending
        set((state) => {
          const newRequests = new Map(state.confirmationRequests);
          newRequests.delete(confirmationId);
          return { confirmationRequests: newRequests };
        });

        // Add system message with tool_call_id for tracking
        const systemMessage: Message = {
          id: `msg-${Date.now()}`,
          role: 'system',
          content: `${approved ? 'Approved' : 'Denied'} action`,
          timestamp: new Date(),
          tool_call_id: confirmationRequest?.tool_call_id, // Add tool_call_id to track execution
        };

        set((state) => ({ messages: [...state.messages, systemMessage] }));
      },
      
      // Store uploaded image for cleanup
      storeUploadedImage: (toolCallId: string, imageUrl: string) => {
        set((state) => {
          const newUploadedImages = new Map(state.uploadedImages);
          newUploadedImages.set(toolCallId, imageUrl);
          return { uploadedImages: newUploadedImages };
        });
      },

      // New session
      startNewChat: async () => {
        const state = get();

        // Disconnect current session
        if (state.sessionId) {
          websocketService.disconnect();
        }

        // Clear messages and reset state
        set({
          sessionId: null,
          threadId: null,
          messages: [],
          confirmationRequests: new Map(),
          toolExecutions: new Map(),
          uploadedImages: new Map(),
          messageQueue: [],
          connectionState: 'offline',
          isConnected: false,
          isConnecting: false,
          connectionError: null,
        });

        // Create new session
        const { selectedCompanyId } = await import('@/app/lib/stores/useAppStore').then(m => m.useAppStore.getState());
        if (selectedCompanyId) {
          await get().createSession(selectedCompanyId);
        }
      },

      // Cleanup
      reset: () => {
        const state = get();
        websocketService.disconnect();
        set({
          ...INITIAL_STATE,
          panelWidth: state.panelWidth, // Preserve UI preferences
        });
      },
    }),
    {
      name: 'copilot-storage',
      partialize: (state) => ({
        panelWidth: state.panelWidth,
      }),
    }
  )
);